import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "./CardsListSection.module.scss";

/**
 * Ein spezieller Sentinel für Infinite Scroll in CardsListSection
 * Beobachtet den Viewport und löst Ladeereignisse aus, wenn er sichtbar wird
 */
const CardsListSentinel = ({ 
	onIntersect, 
	hasMorePages, 
	isLoading,
	isLoadingMore,
	initialDataLoaded = false, // New prop to ensure observer is only set up after initial data is loaded
	loadMoreText = "Weitere Einträge werden geladen...",
	endMessage = "Sie haben das Ende der Liste erreicht.",
	showEndMessage = false 
}) => {
	const sentinelRef = useRef(null);
	const observerRef = useRef(null);
	const [isObserverActive, setIsObserverActive] = useState(false); // Start as inactive until data is loaded
	const triggerLockRef = useRef(false); // Sperrmechanismus für Trigger
	
	// Disconnect und reconnect Observer-Funktion
	const disconnectObserver = useCallback(() => {
		if (observerRef.current) {
			observerRef.current.disconnect();
			observerRef.current = null;
		}
	}, []);
	
	// Observer einrichten mit verbessertem Locking-Mechanismus
	const setupObserver = useCallback(() => {
		// Cleanup any existing observer first
		disconnectObserver();
		
		// Do not setup observer if loading is in progress, no more pages, or initial data not loaded
		if (!hasMorePages || isLoading || isLoadingMore || triggerLockRef.current || !initialDataLoaded) {
			return;
		}
		
		const currentSentinel = sentinelRef.current;
		if (!currentSentinel || !window.IntersectionObserver) return;
		
		// Create a new observer with enhanced locking logic
		const observer = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				
				// Only trigger if:
				// 2. More pages are available
				// 3. Not currently loading
				// 4. Trigger isn't locked
				// 5. Initial data is loaded
				if (entry.isIntersecting && 
					hasMorePages && 
					!isLoading && 
					!isLoadingMore && 
					!triggerLockRef.current &&
					initialDataLoaded) {
					
					// Set lock FIRST to prevent multiple triggers
					triggerLockRef.current = true;
					
					// Also disconnect observer immediately to be absolutely sure no multiple triggers happen
					disconnectObserver();
					
					console.log("📜 Sentinel beobachtet: Trigger für mehr Inhalte (mit Sperrung)", {
						sentinelTop: entry.boundingClientRect.top,
						windowHeight: window.innerHeight,
						lockStatus: "Gesperrt"
					});
					
					// CRITICAL: Set observer inactive state
					setIsObserverActive(false);
					
					// Call load callback
					if (onIntersect && typeof onIntersect === "function") {
						onIntersect();
					}
					
					// The lock will be released when loading is complete
					// via the useEffect that watches isLoadingMore and isLoading
				}
			},
			{ 
				rootMargin: '600px 0px', 
				threshold: 0.1
			}
		);
		
		observer.observe(currentSentinel);
		observerRef.current = observer;
		
	}, [hasMorePages, isLoading, isLoadingMore, initialDataLoaded, disconnectObserver, onIntersect]);
	
	// Set up observer when initialDataLoaded changes to true
	useEffect(() => {
		if (initialDataLoaded) {
			// Only activate observer after initial data is loaded
			setIsObserverActive(true);
			setupObserver();
		} else {
			// Ensure observer is disconnected when initialDataLoaded is false
			setIsObserverActive(false);
			disconnectObserver();
		}
		
		return disconnectObserver;
	}, [initialDataLoaded, setupObserver, disconnectObserver]);
	
	// Watch for changes in dependencies that should trigger observer setup/teardown
	useEffect(() => {
		if (isObserverActive && initialDataLoaded) {
			setupObserver();
		}
		
		return () => {
			if (!isObserverActive) {
				disconnectObserver();
			}
		};
	}, [hasMorePages, isObserverActive, setupObserver, disconnectObserver, initialDataLoaded]);
	
	// Explicitly watch loading state changes to manage locks
	useEffect(() => {
		// When loading starts, ensure lock is active
		if (isLoading || isLoadingMore) {
			triggerLockRef.current = true;
			setIsObserverActive(false);
			disconnectObserver();
		} 
		// When loading completes, release lock and re-enable observer
		else {
			// Add a small delay to ensure DOM has updated with new items
			const unlockDelay = setTimeout(() => {
				triggerLockRef.current = false;
				
				// Only set observer active if initial data is loaded
				if (initialDataLoaded) {
					setIsObserverActive(true);
					
					// Only re-setup observer if we have more pages
					if (hasMorePages) {
						setupObserver();
					}
				}
			}, 300); // Small delay to let DOM update
			
			return () => clearTimeout(unlockDelay);
		}
	}, [isLoading, isLoadingMore, hasMorePages, setupObserver, disconnectObserver, initialDataLoaded]);
	
	return (
		<div className={styles.sentinelContainer}>
			{/* Der eigentliche Sentinel, der beobachtet wird */}
			<div 
				ref={sentinelRef} 
				className={styles.sentinel}
				aria-hidden="true" 
				data-locked={triggerLockRef.current ? "true" : "false"}
				data-loading={isLoading || isLoadingMore ? "true" : "false"}
				data-initial-loaded={initialDataLoaded ? "true" : "false"}
			/>
			
			{/* Ladezustand anzeigen */}
			{isLoadingMore && (
				<div className={styles.loadingIndicator}>
					<div className="spinner-border spinner-border-sm text-primary me-2" role="status">
						<span className="visually-hidden">Lädt...</span>
					</div>
					<span>{loadMoreText}</span>
				</div>
			)}
			
			{/* Ende-Nachricht anzeigen, aber nur wenn explizit angefordert */}
			{showEndMessage && (
				<div className={styles.endMessage}>
					{endMessage}
				</div>
			)}
		</div>
	);
};

export default CardsListSentinel;
